import { useBunja } from 'bunja/react';
import { useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import { fireYourPositionsManageClickEvent } from '@flipster/features/src/position-close-modal/segment';
import PnlModal from '@flipster/features/src/shared/pnl-modal';
import { PnlShareModal } from '@flipster/features/src/shared/pnl-share-modal';
import { tradingSessionCheckBunja } from '@flipster/features/src/shared/trading-suspension/state';
import { useOverlay } from '@flipster/overlay';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, vstack } from '@flipster/styled-system/patterns';
import { getFlipsterConfigsV1Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { PositionEvent } from '@flipster/utils/segment/events/position';
import { ProfitBoostEvent } from '@flipster/utils/segment/events/profit-boost';

import AddPositionModal from '@web/app/[locale]/(web)/_modal/AddPositionModal';
import AdjustLeverageModal from '@web/app/[locale]/(web)/_modal/AdjustLeverageModal';
import AdjustMarginModal from '@web/app/[locale]/(web)/_modal/AdjustMarginModal';

import { yourPositionsBunja } from '../../../state';
import { pendingPositionTableBunja } from '../../state';
import { openPositionRowDataBunja } from './row-bunja';
import { useOpenPositionSnapshot } from './use-open-position-snapshot';

/**
 * 열린 포지션 행의 액션(모달 오픈·segment) 모음. 행 Provider(SymbolScope, PositionSlotScope) 아래의
 * 셀에서 호출한다. 모든 핸들러는 클릭 시점에 source 스냅샷을 읽으므로(useAtomCallback)
 * identity가 tick과 무관하게 안정적이다 — memo 셀의 onClick prop으로 안전하다.
 */
export function useOpenPositionRowActions() {
  const transPositionProfitBoost = useTranslations('Position_profit_boost.r250401');
  const transCommon = useTranslations('Common.r250401');
  const overlay = useOverlay();

  const { symbol, slot } = useBunja(openPositionRowDataBunja);
  const { positionTabTypeAtom } = useBunja(yourPositionsBunja);
  const { pendingPositionTypeAtom } = useBunja(pendingPositionTableBunja);
  const setPositionTab = useSetAtom(positionTabTypeAtom);
  const setPendingPositionType = useSetAtom(pendingPositionTypeAtom);
  const { checkTradable, guardOffHoursAlert } = useBunja(tradingSessionCheckBunja);

  // 클릭 시점에 source 스냅샷을 읽는다(구독 아님) — 핸들러가 tick마다 재생성되지 않게 한다.
  // 스냅샷 필드 조합은 CTA와 공유한다 (useOpenPositionSnapshot).
  const readSnapshot = useOpenPositionSnapshot();

  const pnlShareClick = useCallback(() => {
    const { position: item, instrument } = readSnapshot();
    if (!item || !instrument) return;
    const isBoosting = item.boostExpiryTimestamp !== null;
    overlay.open(({ isOpen, close, unmount }) => (
      <PnlShareModal
        isOpen={isOpen}
        onClose={close}
        position={{
          type: 'perpetual',
          status: 'open',
          symbol,
          side: item.side,
          initLeverage: item.initLeverage,
          entryPrice: item.avgEntryPrice,
          evaluationPrice: item.markPrice,
          pnl: item.unrealizedPnl,
          pnlPercentage: item.calcUnrealizedPnlPercent,
          profitBoostMode: isBoosting,
        }}
        onExit={unmount}
      />
    ));
    segment.track(PositionEvent.PnLShareClick, {
      position: item.side === 'Long' ? 'buy' : 'sell',
      position_status: 'open',
      symbol,
      leverage: item.initLeverage,
      unrealized_pnl: item.unrealizedPnl,
      unrealized_pnl_percentage: item.calcUnrealizedPnlPercent,
      current_price: item.markPrice,
      trade_type: 'perpetual',
      currency: instrument.displayShortName,
      profit_boost_mode: isBoosting,
    });
  }, [readSnapshot, overlay, symbol]);

  // PnL 값 클릭 시 브레이크다운 모달 (기존 PnlModeDisplay 내부 동작을 셀 분해하며 액션 훅으로 이전)
  const openPnlBreakdownModal = useCallback(() => {
    const { position: item } = readSnapshot();
    if (!item) return;
    segment.track(PositionEvent.PositionPnlDetailsClick);
    overlay.open(({ isOpen, close, unmount }) => (
      <PnlModal symbol={symbol} positionId={item.positionId} isOpen={isOpen} onClose={close} onExit={unmount} />
    ));
  }, [readSnapshot, overlay, symbol]);

  const openAddPositionModal = useCallback(async () => {
    const { position: item } = readSnapshot();
    if (!item) return;
    if (!checkTradable()) return;
    if (!(await guardOffHoursAlert('addPosition'))) return;

    overlay.open(({ isOpen, close, unmount }) => (
      <AddPositionModal
        isOpen={isOpen}
        onClose={close}
        position={item}
        onSuccess={(orderType) => {
          switch (orderType) {
            case 'market':
              setPositionTab('open');
              break;
            case 'limit':
              setPendingPositionType('limit');
              setPositionTab('pending');
              break;
            default:
              break;
          }
        }}
        onExit={unmount}
      />
    ));
  }, [readSnapshot, overlay, setPositionTab, setPendingPositionType, checkTradable, guardOffHoursAlert]);

  const handleAddPosition = useCallback(() => {
    const { position: item, user } = readSnapshot();
    if (!item) return;
    const isBoosting = item.boostExpiryTimestamp !== null;
    let isChecked = false;

    const storageKey = user && getFlipsterConfigsV1Key(user.userId, 'skipAdditionalOrderWarningInProfitBoost');
    const canSkip = (storageKey && (getLocalStorage(storageKey) as boolean)) || false;

    if (isBoosting && !canSkip) {
      return dialog.open({
        title: {
          label: (
            <div className={hstack({ gap: 4 })}>
              <CautionCircleIcon size={24} className={css({ color: 'content.state.warning' })} />
              {transPositionProfitBoost('Add_position_warning_title')}
            </div>
          ),
        },
        content: (
          <div className={vstack({ gap: 16, alignItems: 'flex-start' })}>
            <div>{transPositionProfitBoost('Add_position_warning_description')}</div>
            <Checkbox
              onChange={(e) => {
                isChecked = e.target.checked;
              }}
              css={{ color: 'content.default.level-2' }}
            >
              {transCommon('Dont_show_warning_again')}
            </Checkbox>
          </div>
        ),
        buttons: {
          primary: (
            <Dialog.Button
              hierarchy='primary'
              label={transCommon('Confirm')}
              onClick={() => {
                segment.track(ProfitBoostEvent.ProfitBoostAddPositionConfirmClick, {
                  position_id: item.positionId,
                  event_location: 'order_form_page',
                });
                if (isChecked && storageKey) setLocalStorage(storageKey, true);
                openAddPositionModal();
              }}
            />
          ),
          secondary: <Dialog.Button hierarchy='secondary' color='neutral' label={transCommon('Cancel')} />,
        },
      });
    } else {
      openAddPositionModal();
    }
  }, [readSnapshot, openAddPositionModal, transPositionProfitBoost, transCommon]);

  const openAdjustMarginModal = useCallback(async () => {
    const { position: item, account, showAllSymbol } = readSnapshot();
    if (!item) return;
    if (!checkTradable()) return;
    if (!(await guardOffHoursAlert('adjustMargin'))) return;
    overlay.open(({ isOpen, close, unmount }) => (
      <AdjustMarginModal
        isOpen={isOpen}
        onClose={close}
        symbol={symbol}
        slot={slot}
        page='trade_page'
        onSuccess={close}
        onExit={unmount}
      />
    ));
    if (!account) return;
    fireYourPositionsManageClickEvent('Margin', {
      ...item,
      mode: account.tradeMode,
      isShowAll: showAllSymbol,
      isProfitBoostMode: Boolean(item.boostExpiryTimestamp),
    });
  }, [readSnapshot, checkTradable, guardOffHoursAlert, overlay, slot, symbol]);

  const openAdjustLeverageModal = useCallback(async () => {
    const { position: item, account, showAllSymbol } = readSnapshot();
    if (!item) return;
    if (!checkTradable()) return;
    if (!(await guardOffHoursAlert('adjustLeverage'))) return;
    overlay.open(() => <AdjustLeverageModal type='position' from='open-positions' position={item} />);
    if (!account) return;
    fireYourPositionsManageClickEvent('Leverage', {
      ...item,
      mode: account.tradeMode,
      isShowAll: showAllSymbol,
      isProfitBoostMode: Boolean(item.boostExpiryTimestamp),
    });
  }, [readSnapshot, overlay, checkTradable, guardOffHoursAlert]);

  return { pnlShareClick, openPnlBreakdownModal, handleAddPosition, openAdjustMarginModal, openAdjustLeverageModal };
}
